










import Debug from 'debug'
import { Vue, Component, Prop, Emit, Watch } from 'vue-property-decorator';
import { SerializedFillIn, Gap } from '@xlearn/common';
import SelectComponent from '@/app/shared/components/select-component.vue';
import { Dictionary } from 'lodash';
import { FillInAnswer, FillInAnswers, FillInAnswerResults } from './fill-in-interfaces';
import * as Api from "@/app/question/components/api";
import HelpPopupComponent from "@/app/presenter/components/help-popup-component.vue";
import { TranslateResult } from 'vue-i18n';
import i18n from '@/plugins/i18n';

const debug = Debug('fillin-question-component');

export type FillInCorrectAnswer = Gap[]

@Component({
  components: {
    HelpPopupComponent,
  }
})
export default class FillInQuestionComponent extends Vue
implements Api.QuestionComponent<SerializedFillIn, FillInAnswer, {}, FillInCorrectAnswer, FillInAnswers> {
  @Prop({
    required: true,
  })
  public question: Api.Question<SerializedFillIn>;

  @Prop({
    required: true,
  })
  public evaluated: boolean;

  @Prop({
    required: true,
  })
  public answer: Api.Answer<FillInAnswer> | null;

  @Prop({
    required: true,
  })
  public showSolution: boolean;

  @Prop({
    required: true,
  })
  public state: Api.State<{}> | null;

  private selectComponentsByGapId: Dictionary<SelectComponent> = {};
  private answers: FillInAnswers = {};
  private answerResults: FillInAnswerResults = {};
  private selectComponent: SelectComponent;

  public solutionProps() {
    return {
      userAnswer: this.answers,
      correctAnswer: this.question.content.gaps,
    };
  };

  private mounted(): void {
    debug('mounted');

    this.setDefaults();

    const element = this.createElement();

    const container = <HTMLElement>this.$refs['container']
    container.appendChild(element);
  }

  @Emit('answer-changed')
  public emitAnswerChanged(): Api.AnswerChangedPayload<FillInAnswer> {

    let score = 0;
    for (let i in this.answerResults) {
      if (this.answerResults[i]) {
        score += this.question.content.gaps.filter((f: Gap) => f.id === i)[0].score
      }
    }

    const details = {
      score,
      answer: {
        answers: this.answers,
        answerResults: this.answerResults,
      }
    };

    debug('emit-answer-changed', details);

    return details
  }

  @Emit('state-changed')
  public emitStateChanged(): Api.StateChangedPayload<{}> {

    debug('emit-state-changed');

    return {
      state: {}
    };
  }

  @Watch('evaluated')
  private onEvaluatedChanged(newValue: boolean): void {
    debug('on-evaluated-changed', newValue);

    Object.values(this.selectComponentsByGapId).forEach((component) => {
      component.evaluated = newValue;

      if (!newValue) {
        component.setDefaultValue();
      }
    });
  }

  @Watch('answer')
  private answerChanged(newValue: Api.Answer<FillInAnswer> | null): void {
    debug('answer-changed', newValue);

    if (!this.answer) {
      this.resetAllSelectValues();
      this.setDefaults();
    }
  }

  private setDefaults(): void {
    this.answers = this.answer?.answer.answers || {};
    this.answerResults = this.answer?.answer.answerResults || {};
  }

  private resetAllSelectValues() {
    debug('reset-all-selected-values');

    Object.values(this.selectComponentsByGapId).forEach((component) => {
      component.evaluated = false;
      component.setDefaultValue();
    });

    for (let i in this.selectComponentsByGapId) {
      this.selectComponentsByGapId[i].setDefaultValue();
    }
  }

  private createElement(): Element {
    const $container = $('<div class="fill-in-component-content"></div>').html(this.question.content.content);

    this.question.content.gaps.forEach((gap: Gap) => {
      const selectComponent = this.createSelectComponent(gap);

      this.selectComponentsByGapId[gap.id] = selectComponent;

      const $gap = $(`.fill-in-gap.component[data-id="${gap.id}"]`, $container);
      $gap.replaceWith(selectComponent.$el);
    });

    return $container[0];
  }

  private createSelectComponent(gap: Gap): SelectComponent {
    const options: { [value: string]: string } = gap.answers.reduce(
      (prev: { [value: string]: string }, current) => {
        prev[current.id] = current.text;
        return prev;
      }, {});

    const answers = this.answers;
    let correct: boolean = false;
    let selectedValue: string | null = null;
    if (this.answers[gap.id]) {
      selectedValue = this.answers[gap.id];
      if (this.answerResults[gap.id]) {
        correct = true;
      }
    }

    const selectComponent = new SelectComponent({
      propsData: {
        options,
        selectedValue,
        correct,
        evaluated: this.evaluated,
      }
    });
    selectComponent.$mount();

    selectComponent.$on('change', (answerId: string) => {
      const gapId: string = gap.id;
      this.answers[gapId] = answerId;
      const correct: boolean = answerId === gap.goodAnswerId;
      this.answerResults[gapId] = correct;
      selectComponent.correct = correct;

      this.emitAnswerChanged();
    });

    return selectComponent;
  }

  private beforeDestroy() {
    debug('before-destroy');

    for (let i in this.selectComponentsByGapId) {
      this.selectComponentsByGapId[i].$off('change');
    }
  }

  private t(key: string, params: any = {}): TranslateResult {
    return i18n.t(key, params);
  }
}
